<template>
  <div class="forwarding-setup">
    <h1>Set Up Email Forwarding</h1>

    <!-- Step 1: Enter email address -->
    <div v-if="step === 1" class="setup-step">
      <div class="step-header">
        <span class="step-number">1</span>
        <h2>Enter your email address</h2>
      </div>
      <p class="step-description">
        Enter the email address you'll forward retailer receipts from.
        This should be the email where you receive order confirmations.
      </p>

      <div class="input-group">
        <input
          type="email"
          v-model="forwardingEmail"
          placeholder="your-email@gmail.com"
          :disabled="loading"
          @keyup.enter="initiateSetup"
        />
        <button
          @click="initiateSetup"
          :disabled="loading || !isValidEmail"
          class="btn-primary"
        >
          {{ loading ? 'Setting up...' : 'Continue' }}
        </button>
      </div>

      <div v-if="error" class="error-alert">{{ error }}</div>
    </div>

    <!-- Step 2: Instructions -->
    <div v-if="step === 2" class="setup-step">
      <div class="step-header">
        <span class="step-number">2</span>
        <h2>Set up Gmail forwarding</h2>
      </div>

      <div class="instructions-card">
        <p class="instruction-intro">
          Follow these steps to set up email forwarding in Gmail:
        </p>

        <ol class="instruction-list">
          <li>
            <a :href="gmailSettingsUrl" target="_blank" rel="noopener">
              Open Gmail Forwarding Settings
            </a>
          </li>
          <li>Click "Add a forwarding address"</li>
          <li>
            Enter this address:
            <code class="central-inbox">{{ centralInbox }}</code>
            <button @click="copyInbox" class="btn-copy" title="Copy to clipboard">
              {{ copied ? 'Copied!' : 'Copy' }}
            </button>
          </li>
          <li>Click "Next" and then "Proceed"</li>
          <li>Gmail will send a confirmation email</li>
          <li>Return to <strong>Settings</strong> to complete verification</li>
        </ol>

        <div class="next-step-info">
          <p>
            After Gmail sends the confirmation email, go to <strong>Settings</strong>
            to view the verification link and complete setup.
          </p>
        </div>

        <div class="action-buttons">
          <button @click="step = 1" class="btn-text">
            &larr; Back
          </button>
          <router-link to="/settings" class="btn-primary">
            Go to Settings
          </router-link>
        </div>

        <div v-if="error" class="error-alert">{{ error }}</div>
      </div>
    </div>

    <!-- Step 3: Success -->
    <div v-if="step === 3" class="setup-step success-step">
      <div class="success-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
          <polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
      </div>
      <h2>Forwarding Verified!</h2>
      <p>Your email forwarding is now set up.</p>

      <div class="next-steps-card">
        <h3>Final Step: Create a Gmail Filter</h3>
        <p>
          Set up Gmail filters to automatically forward retailer receipt emails:
        </p>
        <ol class="instruction-list">
          <li>
            <a :href="gmailFilterUrl" target="_blank" rel="noopener">
              Create a new Gmail filter
            </a>
          </li>
          <li>In the "From" field, enter: <code>auto-confirm@amazon.com OR target@em.target.com OR help@walmart.com OR costco@online.costco.com OR BestBuyInfo@emailinfo.bestbuy.com</code></li>
          <li>Click "Create filter"</li>
          <li>Check "Forward it to" and select <code>{{ centralInbox }}</code></li>
          <li>Optionally check "Also apply filter to matching conversations"</li>
          <li>Click "Create filter"</li>
        </ol>
      </div>

      <div class="action-buttons">
        <router-link to="/settings" class="btn-primary">
          Done
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { forwardingAPI } from '../services/api'

const router = useRouter()

const step = ref(1)
const forwardingEmail = ref('')
const centralInbox = ref('')
const loading = ref(false)
const error = ref(null)
const copied = ref(false)

const isValidEmail = computed(() => {
  const email = forwardingEmail.value.trim()
  return email && email.includes('@') && email.includes('.')
})

const gmailSettingsUrl = 'https://mail.google.com/mail/u/0/#settings/fwdandpop'
const gmailFilterUrl = 'https://mail.google.com/mail/u/0/#settings/filters'

onMounted(async () => {
  // Check if already verified
  try {
    const response = await forwardingAPI.getStatus()
    if (response.data.forwarding_verified) {
      step.value = 3
      centralInbox.value = response.data.central_inbox
    } else if (response.data.has_pending_confirmation) {
      // Has pending confirmation - redirect to Settings to complete
      router.push('/settings')
    } else {
      centralInbox.value = response.data.central_inbox
    }
  } catch (err) {
    console.error('Error checking forwarding status:', err)
  }
})

const initiateSetup = async () => {
  if (!isValidEmail.value) return

  loading.value = true
  error.value = null

  try {
    const response = await forwardingAPI.initiateSetup()

    if (response.data.forwarding_verified) {
      step.value = 3
    } else {
      centralInbox.value = response.data.central_inbox
      step.value = 2
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to initiate setup'
  } finally {
    loading.value = false
  }
}

const copyInbox = async () => {
  try {
    await navigator.clipboard.writeText(centralInbox.value)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}
</script>

<style scoped>
.forwarding-setup {
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem;
}

.forwarding-setup h1 {
  color: #2c3e50;
  margin-bottom: 2rem;
}

.setup-step {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.step-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #3498db;
  color: white;
  border-radius: 50%;
  font-weight: bold;
  flex-shrink: 0;
}

.step-header h2 {
  color: #2c3e50;
  font-size: 1.25rem;
  margin: 0;
}

.step-description {
  color: #7f8c8d;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.input-group {
  display: flex;
  gap: 0.75rem;
}

.input-group input {
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
}

.input-group input:focus {
  outline: none;
  border-color: #3498db;
}

.btn-primary {
  padding: 0.75rem 1.5rem;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  white-space: nowrap;
}

.btn-primary:hover:not(:disabled) {
  background: #2980b9;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-text {
  background: none;
  border: none;
  color: #3498db;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0.5rem;
}

.btn-text:hover {
  text-decoration: underline;
}

.back-btn {
  margin-top: 1rem;
}

.error-alert {
  background: #fee2e2;
  color: #c0392b;
  padding: 0.75rem 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  font-size: 0.9rem;
}

.instructions-card {
  margin-top: 1rem;
}

.instruction-intro {
  color: #2c3e50;
  margin-bottom: 1rem;
}

.instruction-list {
  color: #2c3e50;
  line-height: 2;
  padding-left: 1.5rem;
}

.instruction-list li {
  margin-bottom: 0.5rem;
}

.instruction-list a {
  color: #3498db;
}

.instruction-list code {
  background: #f8f9fa;
  padding: 0.2rem 0.5rem;
  border-radius: 3px;
  font-size: 0.9rem;
  color: #e74c3c;
}

.next-step-info {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #e8f4fc;
  border-radius: 6px;
  border-left: 4px solid #3498db;
}

.next-step-info p {
  margin: 0;
  color: #2c3e50;
  font-size: 0.95rem;
}

.instructions-card .action-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
}

.central-inbox {
  display: inline-block;
  background: #e8f4fc;
  color: #2980b9;
  font-weight: 500;
}

.btn-copy {
  margin-left: 0.5rem;
  padding: 0.2rem 0.5rem;
  background: #ecf0f1;
  border: none;
  border-radius: 3px;
  font-size: 0.8rem;
  cursor: pointer;
}

.btn-copy:hover {
  background: #bdc3c7;
}

.success-step {
  text-align: center;
}

.success-icon {
  color: #27ae60;
  margin-bottom: 1rem;
}

.success-step h2 {
  color: #27ae60;
  margin-bottom: 0.5rem;
}

.success-step > p {
  color: #7f8c8d;
  margin-bottom: 2rem;
}

.next-steps-card {
  text-align: left;
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
}

.next-steps-card h3 {
  color: #2c3e50;
  font-size: 1rem;
  margin: 0 0 0.5rem 0;
}

.next-steps-card > p {
  color: #7f8c8d;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.action-buttons {
  display: flex;
  justify-content: center;
}
</style>
